import type {
  ActingParty,
  PersonInvolvement,
  PersonRecordCreators,
} from "../types/involvement.js";
import { isInvolvementHeldBy } from "./isInvolvementHeldBy.js";
import { isInvolvementLive } from "./isInvolvementLive.js";
import { personActionsFromOrganisationAdministration } from "./personActionsFromOrganisationAdministration.js";

/**
 * Whether one party, acting for one organisation in the roles given, may
 * archive a person and unarchive them.
 *
 * The person's responsible user may, whatever their role, and so may an
 * organisation admin of the organisation administering the person
 * (`personActionsFromOrganisationAdministration`). Not derived from
 * `personActionsConveyedByInvolvementKind`: an action conveyed by the duty of
 * care would reach every user in the responsible user role at that
 * organisation, not only the person's responsible user.
 */
export const mayArchivePerson = (
  person: PersonRecordCreators,
  involvements: readonly Pick<
    PersonInvolvement,
    "kind" | "party" | "endedAt" | "expiresAt"
  >[],
  party: ActingParty,
  now: Date = new Date(),
): boolean => {
  const live = involvements.filter((involvement) =>
    isInvolvementLive(involvement, now),
  );

  const runsTheCase = live.some(
    (involvement) =>
      involvement.kind === "responsible_user" &&
      isInvolvementHeldBy(involvement, party),
  );

  const administersIt =
    personActionsFromOrganisationAdministration(
      person,
      involvements,
      party,
      now,
    ).length > 0;

  return runsTheCase || administersIt;
};
